import * as twgl from 'twgl.js';

import { wbgl_type } from './Wbgl.js';
import { scrn_type } from './Scrn.js';
import { Shdr, shdr_type } from './Shdr.js';
import { Rndm } from './Rndm.js';
import { Arry } from './Arry.js';

export interface gl_image_type {
    id: string;
    width: number;
    height: number;
    min_y: number;
    renders: boolean;
    uniforms: {
        u_position: number[];
        u_screen: number[];
        u_texture: WebGLTexture;
        image_a: number;
        image_mask: number;
        u_mask_pos: number[];
        u_mask_size: number[];
        u_mask_radius: number;
        [key: string]: any;
    };
    kill: () => void;
    render: () => void;
}

let _shader: shdr_type | null = null;

export const gl_Image = {

    list: {} as Record<string, WebGLTexture>,

    init: function (Wbgl: wbgl_type): void {
        _shader = Shdr.make(Wbgl, {
            id: "gl_image",
            vertex: `#version 300 es
                precision mediump float;
                in vec2 position;
                in vec2 uv;
                uniform vec2 u_screen;
                uniform vec2 u_position;
                out vec2 vUv;
                out vec2 vWorld;
                void main() {
                    vUv = uv;
                    vWorld = position + u_position;
                    gl_Position = vec4(vWorld * u_screen, 0.0, 1.0);
                }`,
            fragment: `#version 300 es
                precision mediump float;
                uniform sampler2D u_texture;
                uniform float image_a;
                uniform int  image_mask;
                uniform vec2 u_mask_pos;
                uniform vec2 u_mask_size;
                uniform float u_mask_radius;
                in vec2 vUv;
                in vec2 vWorld;
                out vec4 fragColor;
                void main() {
                    vec4 c = texture(u_texture, vUv);
                    float a = c.a * image_a;
                    if (image_mask == 1) {
                        vec2 d = abs(vWorld - u_mask_pos) - u_mask_size + u_mask_radius;
                        float dist = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - u_mask_radius;
                        a *= smoothstep(0.001, 0.0, dist);
                    }
                    fragColor = vec4(c.rgb, a);
                }`
        });
    },

    load: async function (Wbgl: wbgl_type, src: string): Promise<void> {

        if (!_shader) this.init(Wbgl);

        const img = new Image();
        await new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.src = src;
        });

        this.list[src] = twgl.createTexture(Wbgl.context, {
            src: img,
            min: Wbgl.context.LINEAR,
            mag: Wbgl.context.LINEAR,
            wrap: Wbgl.context.CLAMP_TO_EDGE
        });
    },

    make: function (Wbgl: wbgl_type, Scrn: scrn_type, param: any): gl_image_type {

        if (!_shader) this.init(Wbgl);

        const { image_src, image_size = 0.05, image_align_x = 0, image_x = 0, image_y = 0, image_a = 1, image_mask = 1 } = param;

        const hw = image_size / 2;
        const hh = image_size / 2;

        // Horizontal anchor: -1 = left, 0 = center, 1 = right
        const ox = image_align_x === -1 ? 0 : image_align_x === 1 ? -image_size : -hw;

        // Two triangles covering [ox+image_x, ox+image_x+image_size] x [-hh+image_y, hh+image_y]
        const x0 = ox + image_x, x1 = ox + image_x + image_size;
        const y0 = -hh + image_y, y1 = hh + image_y;
        const positions = new Float32Array([x0, y0, x1, y0, x0, y1, x1, y0, x1, y1, x0, y1]);
        const uvs = new Float32Array([0, 0, 1, 0, 0, 1, 1, 0, 1, 1, 0, 1]);

        const bufferInfo = twgl.createBufferInfoFromArrays(Wbgl.context, {
            position: { numComponents: 2, data: positions },
            uv: { numComponents: 2, data: uvs },
        });

        // VAO keeps attributes off the default VAO (avoids warn + flicker on kill)
        const vao = twgl.createVAOFromBufferInfo(Wbgl.context, _shader!.compiled, bufferInfo);

        const t: gl_image_type = {
            id: param.id || Rndm.id(),
            width: image_size,
            height: image_size,
            min_y: -hh,
            renders: param.renders ?? true,
            uniforms: {
                u_position: [0, 0],
                u_screen: Scrn.screen,
                u_texture: gl_Image.list[image_src],
                image_a,
                image_mask,
                u_mask_pos: [0, 0],
                u_mask_size: [0, 0],
                u_mask_radius: 0,
            },
            kill: function () {
                Arry.remove.id([] as any, this.id); // standalone list not used
            },
            render: function () {
                const gl = Wbgl.context;
                gl.useProgram(_shader!.compiled.program);
                gl.bindVertexArray(vao);
                twgl.setUniforms(_shader!.compiled, this.uniforms);
                twgl.drawBufferInfo(gl, bufferInfo);
                gl.bindVertexArray(null);
            }
        };

        return t;
    },
};
